import { useScript } from '#imports'

import { noop } from '@backmarket/utils/function/noop'

import { NETHONE_SCRIPT_URL } from '../config/nethone/constants'
import {
  getOrSetNethoneAttemptReference,
  resetNethoneAttemptReference,
} from '../utils/nethoneAttemptReference'

type UseNethoneOptions = {
  /**
   * When to trigger the script loading.
   * - `'onNuxtReady'`: Automatically load when Nuxt is ready (default)
   * - `'manual'`: Load manually by calling the `load()` function
   */
  trigger?: 'onNuxtReady' | 'manual'
}

type NethoneInitOptions = {
  sensitiveFields?: string[]
  secretFields?: string[]
}

declare global {
  interface Window {
    dftp?: {
      init: (options: {
        attemptReference: string
        sensitiveFields?: string[]
        secretFields?: string[]
      }) => void
      profileCompleted: () => Promise<void>
    }
  }
}

/**
 * Returns a unique attempt reference for Nethone. Loads the library if needed.
 *
 * @example
 * // Auto-load on Nuxt ready (default)
 * const { load, onLoaded } = useNethone()
 * onLoaded(() => {
 *   console.log('Nethone loaded')
 * })
 *
 * @example
 * // Manual loading
 * const { load } = useNethone({ trigger: 'manual' })
 * await load()
 */
export const useNethone = (options: UseNethoneOptions = {}) => {
  if (import.meta.server) {
    // We need cookies to store the attempt reference
    // to simplify we don't load the library on the server
    return {
      attemptReference: '',
      initialize: noop,
      load: noop,
      onLoaded: noop,
      reset: noop,
    }
  }

  const { trigger = 'onNuxtReady' } = options
  const { load, status, onLoaded } = useScript(
    {
      async: true,
      crossorigin: 'use-credentials',
      id: 'nethone-script',
      src: NETHONE_SCRIPT_URL,
    },
    {
      trigger,
    },
  )

  return {
    /**
     * Initialize Nethone dftp and complete profiling
     * @param initOptions - Configuration for sensitive and secret fields
     * @returns Promise that resolves when profiling completes, or null if dftp is not available
     */
    initialize: async (initOptions: NethoneInitOptions = {}) => {
      if (!window.dftp) {
        throw new Error('Nethone dftp object not available')
      }

      const { sensitiveFields = [], secretFields = [] } = initOptions

      window.dftp.init({
        attemptReference: getOrSetNethoneAttemptReference(),
        secretFields,
        sensitiveFields,
      })

      await window.dftp.profileCompleted()
    },

    /** Load the nethone library if needed and return the attempt reference */
    load: async () => {
      if (status.value === 'loaded') {
        return
      }

      try {
        await load()
      } catch (error) {
        // This script can be blocked by extensions like Ghostery.
        // To avoid blocking the payment it will make a silent catch
        // Also, useScript should already have logged the error
      }
    },

    /** Register a callback to be called when the script is loaded */
    onLoaded,

    /** Reset the nethone attempt reference */
    reset: async () => {
      resetNethoneAttemptReference()
    },
  }
}
